<template>
  <div class="card-wrap">
    <h3 class="title">Subir archivo — {{ moduleName }}</h3>
    <p class="sub">
      Sube el archivo exportado desde tu sistema anterior. El mapeo de columnas
      se hace en el siguiente paso.
    </p>

    <div
      v-if="!file"
      :class="['upload-zone', { dragging: isDragging }]"
      @click="$refs.fileInput.click()"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="onDrop"
    >
      <i class="el-icon-upload upload-icon"></i>
      <div class="upload-title">Arrastra tu archivo aquí</div>
      <div class="upload-sub">o haz click para seleccionar</div>
      <div class="format-pills">
        <span v-for="f in acceptedFormats" :key="f" class="pill">{{ f }}</span>
      </div>
      <input
        ref="fileInput"
        type="file"
        :accept="acceptAttr"
        style="display:none"
        @change="onFileChosen"
      />
    </div>

    <div v-else class="file-loaded">
      <div class="file-icon">
        <i class="el-icon-document"></i>
      </div>
      <div class="file-info">
        <div class="file-name">{{ file.name }}</div>
        <div class="file-meta">
          {{ formatBytes(file.size) }} ·
          {{ uploading ? "Procesando..." : "Listo para subir" }}
        </div>
      </div>
      <button class="file-remove" @click="removeFile" :disabled="uploading">
        <i class="el-icon-close"></i>
      </button>
    </div>

    <el-progress
      v-if="uploading"
      :percentage="uploadProgress"
      :stroke-width="6"
      :show-text="false"
      class="mt-3"
    />

    <div v-if="module === 'invoices'" class="hint-card">
      <div class="hint-title">¿Tienes archivos del SRI?</div>
      <div class="hint-text">
        <strong>XML:</strong> facturas autorizadas individuales — mapeo
        automático total.<br />
        <strong>TXT:</strong> reportes "Comprobantes Emitidos / Autorizados"
        (separados por tabs) — el sistema reconoce las columnas
        <code>SERIE_COMPROBANTE</code>, <code>CLAVE_ACCESO</code>,
        <code>FECHA_EMISION</code>, <code>VALOR_SIN_IMPUESTOS</code>,
        <code>IVA</code> e <code>IMPORTE_TOTAL</code> automáticamente.
      </div>
    </div>

    <div class="btn-row">
      <el-button plain @click="$emit('back')">← Atrás</el-button>
      <el-button
        type="primary"
        :loading="uploading"
        :disabled="!file || uploading"
        @click="upload"
      >
        Subir y continuar →
      </el-button>
    </div>
  </div>
</template>

<script>
import api from "@/config/Api";

export default {
  name: "StepUpload",
  props: {
    module: { type: String, required: true }
  },
  data() {
    return {
      file: null,
      isDragging: false,
      uploading: false,
      uploadProgress: 0
    };
  },
  computed: {
    moduleName() {
      const names = {
        catalog: "Catálogo",
        directory: "Directorio",
        invoices: "Facturas"
      };
      return names[this.module] || "";
    },
    acceptedFormats() {
      return this.module === "invoices"
        ? ["CSV", "Excel (.xlsx)", "TXT SRI", "XML SRI"]
        : ["CSV", "Excel (.xlsx)", "TXT"];
    },
    acceptAttr() {
      return this.module === "invoices"
        ? ".csv,.xlsx,.xls,.xml,.txt"
        : ".csv,.xlsx,.xls,.txt";
    }
  },
  methods: {
    onFileChosen(event) {
      const f = event.target.files[0];
      if (f) this.setFile(f);
    },
    onDrop(event) {
      this.isDragging = false;
      const f = event.dataTransfer.files[0];
      if (f) this.setFile(f);
    },
    setFile(f) {
      const allowed = ["csv", "xlsx", "xls", "txt"];
      if (this.module === "invoices") allowed.push("xml");
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (!allowed.includes(ext)) {
        this.$notifications.error({
          message:
            "Formato no soportado. Usa " + this.acceptedFormats.join(", ")
        });
        return;
      }
      if (f.size > 10 * 1024 * 1024) {
        this.$notifications.error({
          message: "El archivo supera 10 MB. Divide el archivo y reintenta."
        });
        return;
      }
      this.file = f;
    },
    removeFile() {
      this.file = null;
      this.uploadProgress = 0;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    formatBytes(bytes) {
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
      return (bytes / 1048576).toFixed(2) + " MB";
    },
    async upload() {
      if (!this.file) return;
      this.uploading = true;
      this.uploadProgress = 0;

      const fd = new FormData();
      fd.append("module", this.module);
      fd.append("file", this.file);

      try {
        const { data } = await api().post("/integrations/imports/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: e => {
            if (e.total) {
              this.uploadProgress = Math.round((e.loaded * 100) / e.total);
            }
          }
        });

        if (!data || !data.session_id) {
          this.$notifications.error({
            message: "El servidor no devolvió la sesión de importación."
          });
          this.uploading = false;
          return;
        }

        this.$emit("uploaded", data);
      } catch (e) {
        this.$notifications.error({
          message:
            (e.response && e.response.data && e.response.data.message) ||
            "No se pudo subir el archivo."
        });
      } finally {
        this.uploading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.card-wrap {
  background: #fff;
  border: 0.5px solid #e4eaf6;
  border-radius: 12px;
  padding: 1.75rem;
}

.title {
  font-size: 17px;
  font-weight: 600;
  color: #2d2b55;
  margin: 0 0 4px;
}

.sub {
  font-size: 13px;
  color: #6e7484;
  margin: 0 0 1.5rem;
}

.upload-zone {
  border: 2px dashed #d3d1c7;
  border-radius: 10px;
  padding: 2.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover,
  &.dragging {
    border-color: #3ecfb4;
    background: #f0fdf9;
  }
}

.upload-icon {
  font-size: 36px;
  color: #2d2b55;
  margin-bottom: 12px;
}

.upload-title {
  font-size: 14px;
  font-weight: 600;
  color: #2d2b55;
  margin-bottom: 4px;
}

.upload-sub {
  font-size: 12px;
  color: #6e7484;
  margin-bottom: 14px;
}

.format-pills {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;

  .pill {
    font-size: 11px;
    padding: 3px 10px;
    border-radius: 20px;
    background: #f7f7f5;
    color: #6e7484;
    border: 0.5px solid #e4eaf6;
  }
}

.file-loaded {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f7f7f5;
  border-radius: 10px;
  padding: 14px 18px;
}

.file-icon {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: #eeedfe;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #534ab7;
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-size: 13px;
  font-weight: 600;
  color: #2d2b55;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-meta {
  font-size: 12px;
  color: #6e7484;
}

.file-remove {
  margin-left: auto;
  border: none;
  background: transparent;
  color: #e24b4a;
  cursor: pointer;
  font-size: 16px;
  padding: 4px 8px;
  border-radius: 6px;

  &:hover {
    background: #fcebeb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.hint-card {
  background: #f7f7f5;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 1.25rem;
}

.hint-title {
  font-size: 12px;
  font-weight: 600;
  color: #2d2b55;
  margin-bottom: 4px;
}

.hint-text {
  font-size: 12px;
  color: #6e7484;
  line-height: 1.5;
}

.btn-row {
  display: flex;
  justify-content: space-between;
  margin-top: 1.5rem;
}

.mt-3 {
  margin-top: 12px;
}
</style>
